import React from 'react';
import {
  AlertTriangle,
  Clock,
  Info,
  Phone,
  RefreshCw,
  ShieldCheck,
  Video,
} from 'lucide-react';
import { EligibilityCheck } from '../../types/eligibility';
import {
  UNKNOWN,
  carveOutMessage,
  formatCheckedAt,
  formatDeductible,
  formatFlag,
  formatMoney,
  formatPercent,
  formatRemainingOfTotal,
  formatX12Date,
  isStale,
  statusPresentation,
} from '../../utils/eligibilityDisplay';

/**
 * Renders one eligibility check.
 *
 * Design rule throughout: never present an absent value as a number. A payer
 * that said nothing about copay shows "Unknown", because staff quote these
 * figures to patients and a confident "$0" is worse than an honest blank.
 */

interface EligibilityResultCardProps {
  check: EligibilityCheck;
  fromCache?: boolean;
  onReverify?: () => void;
  reverifying?: boolean;
}

const COLORS = {
  panel: '#1c2536',
  border: '#374151',
  text: '#f9fafb',
  label: '#9ca3af',
  accent: '#6CA6C1',
  danger: '#ef4444',
  warning: '#D4A017',
};

const Field: React.FC<{ label: string; value: string; emphasis?: boolean }> = ({
  label,
  value,
  emphasis,
}) => (
  <div>
    <div style={{ color: COLORS.label, fontSize: '12px', marginBottom: '2px' }}>{label}</div>
    <div
      style={{
        color: value === UNKNOWN ? COLORS.label : COLORS.text,
        fontSize: emphasis ? '18px' : '14px',
        fontWeight: emphasis ? 600 : 400,
        fontStyle: value === UNKNOWN ? 'italic' : 'normal',
      }}
    >
      {value}
    </div>
  </div>
);

const EligibilityResultCard: React.FC<EligibilityResultCardProps> = ({
  check,
  fromCache = false,
  onReverify,
  reverifying = false,
}) => {
  const status = statusPresentation(check.status);
  const carveOut = carveOutMessage(check);
  const stale = isStale(check);
  const { benefits } = check;

  const telehealth = formatFlag(benefits.telehealthCovered, 'Covered', 'Not covered');
  const priorAuth = formatFlag(benefits.priorAuthRequired, 'Required', 'Not required');

  return (
    <section
      // Announced to screen readers when a result replaces the loading state.
      role="status"
      aria-live="polite"
      aria-label={`Insurance verification result: ${status.label}`}
      style={{
        background: COLORS.panel,
        border: `1px solid ${COLORS.border}`,
        borderRadius: '0.75rem',
        padding: '1.25rem',
        color: COLORS.text,
      }}
    >
      <header
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: '12px',
          flexWrap: 'wrap',
        }}
      >
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <span
              style={{
                backgroundColor: status.background,
                color: status.color,
                padding: '4px 12px',
                borderRadius: '4px',
                fontSize: '13px',
                fontWeight: 500,
              }}
            >
              {status.label}
            </span>
            {benefits.inNetwork === true && (
              <span
                style={{
                  backgroundColor: '#E8F5E9',
                  color: '#2E7D32',
                  padding: '4px 10px',
                  borderRadius: '4px',
                  fontSize: '12px',
                }}
              >
                In network
              </span>
            )}
            {benefits.inNetwork === false && (
              <span
                style={{
                  backgroundColor: '#FFF3E0',
                  color: '#E65100',
                  padding: '4px 10px',
                  borderRadius: '4px',
                  fontSize: '12px',
                }}
              >
                Out of network
              </span>
            )}
          </div>

          <h3 style={{ margin: '10px 0 2px', fontSize: '16px', fontWeight: 600 }}>
            {check.payerName || 'Insurance payer'}
          </h3>
          <div style={{ color: COLORS.label, fontSize: '12px' }}>
            {benefits.planName || 'Plan name not provided'}
            {benefits.groupNumber ? ` · Group ${benefits.groupNumber}` : ''}
          </div>
        </div>

        {onReverify && (
          <button
            type="button"
            onClick={onReverify}
            disabled={reverifying}
            aria-label="Re-check coverage with the payer"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              background: 'transparent',
              border: `1px solid ${COLORS.border}`,
              borderRadius: '0.5rem',
              color: COLORS.accent,
              padding: '8px 12px',
              fontSize: '13px',
              cursor: reverifying ? 'wait' : 'pointer',
              opacity: reverifying ? 0.6 : 1,
            }}
          >
            <RefreshCw size={14} aria-hidden="true" />
            {reverifying ? 'Checking…' : 'Re-check'}
          </button>
        )}
      </header>

      {check.statusReason && check.status !== 'active' && (
        <p style={{ margin: '12px 0 0', fontSize: '13px', color: COLORS.text }}>
          {check.statusReason}
        </p>
      )}

      {/* The carve-out notice sits above the numbers on purpose: it changes what
          those numbers mean, so it must not be read after them. */}
      {carveOut && (
        <div
          style={{
            marginTop: '14px',
            padding: '12px',
            borderRadius: '0.5rem',
            border: `1px solid ${carveOut.tone === 'warning' ? COLORS.warning : COLORS.accent}`,
            background: carveOut.tone === 'warning' ? 'rgba(212,160,23,0.10)' : 'rgba(108,166,193,0.10)',
            display: 'flex',
            gap: '10px',
            alignItems: 'flex-start',
          }}
        >
          {carveOut.tone === 'warning' ? (
            <AlertTriangle size={16} aria-hidden="true" style={{ color: COLORS.warning, flexShrink: 0, marginTop: '2px' }} />
          ) : (
            <Info size={16} aria-hidden="true" style={{ color: COLORS.accent, flexShrink: 0, marginTop: '2px' }} />
          )}
          <div style={{ fontSize: '13px', lineHeight: 1.5 }}>
            {carveOut.text}
            {check.carveOut.phone && (
              <div
                style={{
                  marginTop: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  color: COLORS.label,
                }}
              >
                <Phone size={12} aria-hidden="true" />
                {check.carveOut.phone}
              </div>
            )}
          </div>
        </div>
      )}

      {check.status !== 'error' && (
        <div
          style={{
            marginTop: '16px',
            display: 'grid',
            gap: '14px',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          }}
        >
          <Field label="Copay" value={formatMoney(benefits.copay)} emphasis />
          <Field label="Coinsurance" value={formatPercent(benefits.coinsurancePercent)} emphasis />
          <Field label="Deductible" value={formatDeductible(benefits)} />
          <Field
            label="Out of pocket"
            value={formatRemainingOfTotal(
              benefits.outOfPocketRemaining,
              benefits.outOfPocketTotal
            )}
          />
          <Field
            label="Visit limit"
            value={formatRemainingOfTotal(benefits.visitLimitRemaining, benefits.visitLimitTotal)}
          />
          <Field label="Prior authorization" value={priorAuth} />
        </div>
      )}

      {check.status !== 'error' && (
        <div
          style={{
            marginTop: '16px',
            paddingTop: '14px',
            borderTop: `1px solid ${COLORS.border}`,
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            flexWrap: 'wrap',
          }}
        >
          <Video
            size={16}
            aria-hidden="true"
            style={{
              color:
                benefits.telehealthCovered === true
                  ? '#10b981'
                  : benefits.telehealthCovered === false
                  ? COLORS.danger
                  : COLORS.label,
            }}
          />
          <span style={{ fontSize: '13px' }}>
            Telehealth:{' '}
            <strong
              style={{
                color:
                  benefits.telehealthCovered === false
                    ? COLORS.danger
                    : benefits.telehealthCovered === true
                    ? COLORS.text
                    : COLORS.label,
                fontStyle: benefits.telehealthCovered === null ? 'italic' : 'normal',
              }}
            >
              {telehealth}
            </strong>
          </span>
          {benefits.telehealthEvidence && (
            <span style={{ color: COLORS.label, fontSize: '12px' }}>
              ({benefits.telehealthEvidence})
            </span>
          )}
          {benefits.telehealthCovered === null && (
            <span style={{ color: COLORS.label, fontSize: '12px' }}>
              — payer did not state; confirm before a virtual visit
            </span>
          )}
        </div>
      )}

      {check.errors && check.errors.length > 0 && (
        <ul
          style={{
            margin: '14px 0 0',
            padding: '12px 12px 12px 28px',
            borderRadius: '0.5rem',
            background: 'rgba(239,68,68,0.10)',
            border: `1px solid ${COLORS.danger}`,
            fontSize: '13px',
          }}
        >
          {check.errors.map((error, index) => (
            <li key={`${error.code}-${index}`} style={{ marginBottom: '4px' }}>
              {error.description || error.code}
              {error.possibleResolutions && (
                <div style={{ color: COLORS.label, fontSize: '12px', marginTop: '2px' }}>
                  {error.possibleResolutions}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      <footer
        style={{
          marginTop: '16px',
          paddingTop: '12px',
          borderTop: `1px solid ${COLORS.border}`,
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          flexWrap: 'wrap',
          color: COLORS.label,
          fontSize: '12px',
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <Clock size={12} aria-hidden="true" />
          Checked {formatCheckedAt(check.checkedAt)}
          {fromCache && ' (cached)'}
        </span>

        {benefits.planEndDate && (
          <span>Plan ends {formatX12Date(benefits.planEndDate)}</span>
        )}

        {check.source === 'mock' && (
          <span style={{ display: 'flex', alignItems: 'center', gap: '5px', color: COLORS.warning }}>
            <ShieldCheck size={12} aria-hidden="true" />
            Simulated data — not a real payer response
          </span>
        )}

        {stale && (
          <span style={{ color: COLORS.warning }}>
            This check has expired — re-check before the appointment.
          </span>
        )}
      </footer>
    </section>
  );
};

export default EligibilityResultCard;
